import {all, isNil} from 'ramda';

const MAX_INPUT_LENGTH = 320;
const MAX_ADDRESS_LENGTH = 254;
const MAX_LOCAL_PART_LENGTH = 64;
const MAX_DOMAIN_LABEL_LENGTH = 63;
const IPV6_GROUPS = 8;
const MAX_COMPRESSED_IPV6_GROUPS = 6;
const IPV6_WITH_IPV4_GROUPS = 6;
const MAX_COMPRESSED_IPV6_WITH_IPV4_GROUPS = 4;
const IPV4_OCTETS = 4;
const IPV6_PREFIX = 'ipv6:';

// One word of the local part (an atom or a quoted string) and the dot after it, if another word follows
const LOCAL_PART_WORD_REGEX =
  /(?:[\x21\x23-\x27\x2A\x2B\x2D\x2F-\x39\x3D\x3F\x5E-\x7E]+|"(?:[\x21\x23-\x5B\x5D-\x7F]|\\[\x20-\x7F])*")(?:\.(?!$)|$)/giy;
const DOMAIN_LABEL_CHARACTERS_REGEX = /^[a-z0-9-]+$/i;
const LETTER_REGEX = /^[a-z]/i;
const IPV4_OCTET_REGEX = /^(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;
const IPV6_GROUP_REGEX = /^[a-f0-9]{1,4}$/i;
const DOMAIN_LITERAL_REGEX = /^\[(.*)\]$/;

/**
 * PHP measures the length in units where a quote around a character and a backslash escape do
 * not count extra.
 */
const countLengthUnits = (value: string) =>
  value.replace(/\\[\x20-\x7F]/g, '_').replace(/"/g, '').length;

/**
 * Dot-separated words, each an atom or a quoted string. The sticky global regex matches word after
 * word from the start, so the local part is valid only when the matches cover all of it.
 */
const isValidLocalPart = (localPart: string) =>
  localPart.match(LOCAL_PART_WORD_REGEX)?.join('') === localPart;

/** Letters, digits and inner hyphens. */
const isValidDomainLabel = (label: string) =>
  DOMAIN_LABEL_CHARACTERS_REGEX.test(label) &&
  !label.startsWith('-') &&
  !label.endsWith('-') &&
  label.length <= MAX_DOMAIN_LABEL_LENGTH;

/** At least two labels; the top-level domain starts with a letter. */
const isValidHostname = (domain: string) => {
  const labels = domain.split('.');
  const topLevelDomain = labels.at(-1) ?? '';

  return labels.length > 1 && all(isValidDomainLabel, labels) && LETTER_REGEX.test(topLevelDomain);
};

const isValidIpv4 = (value: string) => {
  const octets = value.split('.');

  return octets.length === IPV4_OCTETS && all((octet) => IPV4_OCTET_REGEX.test(octet), octets);
};

const parseIpv6Groups = (value: string) => (value === '' ? [] : value.split(':'));

const areValidIpv6Groups = (groups: string[]) =>
  all((group) => IPV6_GROUP_REGEX.test(group), groups);

/** `left::right`, where each side is a colon-separated list of groups. */
const isValidCompressedIpv6 = (value: string, maxGroups: number) => {
  const sides = value.split('::');

  if (sides.length !== 2) {
    return false;
  }

  const groups = [...parseIpv6Groups(sides[0]), ...parseIpv6Groups(sides[1])];

  return groups.length <= maxGroups && areValidIpv6Groups(groups);
};

const isValidIpv6 = (value: string) => {
  const groups = value.split(':');

  return (
    (groups.length === IPV6_GROUPS && areValidIpv6Groups(groups)) ||
    isValidCompressedIpv6(value, MAX_COMPRESSED_IPV6_GROUPS)
  );
};

/** IPv6 groups, each followed by a colon, then an IPv4 address. */
const isValidIpv6WithIpv4 = (value: string) => {
  const ipv4Index = value.lastIndexOf(':') + 1;
  const ipv6Part = value.slice(0, ipv4Index);
  const ipv6Groups = ipv6Part.slice(0, -1);

  if (ipv4Index === 0 || !isValidIpv4(value.slice(ipv4Index))) {
    return false;
  }

  if (!ipv6Part.includes('::')) {
    const groups = ipv6Groups.split(':');
    return groups.length === IPV6_WITH_IPV4_GROUPS && areValidIpv6Groups(groups);
  }

  return isValidCompressedIpv6(
    ipv6Part.endsWith('::') ? ipv6Part : ipv6Groups,
    MAX_COMPRESSED_IPV6_WITH_IPV4_GROUPS
  );
};

const isValidDomainLiteral = (literal: string) => {
  if (!literal.toLowerCase().startsWith(IPV6_PREFIX)) {
    return isValidIpv4(literal);
  }

  const address = literal.slice(IPV6_PREFIX.length);

  return isValidIpv6(address) || isValidIpv6WithIpv4(address);
};

const isValidDomain = (domain: string) => {
  const literal = DOMAIN_LITERAL_REGEX.exec(domain)?.[1];

  return isNil(literal) ? isValidHostname(domain) : isValidDomainLiteral(literal);
};

/**
 * Port of PHP `filter_var($value, FILTER_VALIDATE_EMAIL)` without `FILTER_FLAG_EMAIL_UNICODE`
 * (php-src `ext/filter/logical_filters.c`), the rule the DMS API applies to `format: email`.
 *
 * The PHP pattern is split into flat regexes plus checks in code, because its nested quantifiers
 * backtrack exponentially in JS, which has no PCRE match limit. One known difference: quoted
 * strings accept printable ASCII only, while PHP also accepts control characters there.
 *
 * Format check only: quoted local parts allow `?`, `&` and `=`, so never build a `mailto:` or an
 * `href` from a value only because it passes. The value is not trimmed — trim it before validation.
 */
export function isValidEmail(value: string): boolean {
  const separatorIndex = value.lastIndexOf('@');

  // PHP refuses longer input before it runs the pattern; only ASCII passes, so UTF-16 length = bytes
  if (value.length > MAX_INPUT_LENGTH || separatorIndex === -1) {
    return false;
  }

  const localPart = value.slice(0, separatorIndex);

  return (
    countLengthUnits(value) <= MAX_ADDRESS_LENGTH &&
    countLengthUnits(localPart) <= MAX_LOCAL_PART_LENGTH &&
    isValidLocalPart(localPart) &&
    isValidDomain(value.slice(separatorIndex + 1))
  );
}
